/* eslint-disable react/forbid-component-props */
import classnames from 'classnames';
import { filter, flatten, indexOf, isEmpty, map, orderBy, reduce, take, toLower, isString, } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import useBodyClick from '../../../utils/useBodyClick';
import Scroll from '../../other/Scroll';
import BadgeTooltip from '../../utils/BadgeTooltip';
import Icon from '../../utils/Icon';
import Ripple from '../../utils/Ripple';
import useLayoutDirection from '../../utils/Translations/useLayoutDirection';
import useHelpNote from '../../utils/Translations/useHelpNote';
import useT from '../../utils/Translations/useT';
import SearchField from '../base/SearchField';
import useApplyFilter from './hooks/useApplyFilter';
import MultiselectGroup from './MultiselectGroup';
import MultiselectItem from './MultiselectItem';
import MultiselectSelectAllItem from './MultiselectSelectAllItem';
import styles from './Select.scss';
export default function MultiselectFilter({ value = [], onChange, title, label, icon, iconType, itemId = defaultItemId, itemTitle = defaultItemTitle, itemStatus = defaultItemStatus, showSelectAll = true, size = defaultSize, groupTitleKeys, orderOptionsBy, hasSelectAllDivider = true, opens = 'left', children, liveSearch = false, liveSearchField = 'name', disabled = false, isFullSizeButton = false, classNames = '', buttonClassName = '', options, isEmptyAllowed = false, showStatus = false, maxLimitTooltip = '', textTransformNone = false, labelDividerNone = false, inputClassName = '', hasCountBadge = false, dataCy, }) {
    const t = useT();
    const { isLayoutRTL } = useLayoutDirection();
    const getHelpNoteTooltipTitle = useHelpNote();
    const [expanded, setExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerEl = useRef(null);
    const handleBodyClick = useCallback(() => {
        setExpanded(false);
    }, []);
    useBodyClick({ handler: handleBodyClick, ref: containerEl });
    const handleToggleItem = useCallback((id, selected) => {
        if (!onChange) {
            return;
        }
        if (selected) {
            onChange([...value, id]);
        }
        else {
            const index = indexOf(value, id);
            if (index >= 0) {
                onChange([
                    ...value.slice(0, index),
                    ...value.slice(index + 1),
                ]);
            }
        }
    }, [value, onChange]);
    const handleToggleItems = useCallback((items, selected) => {
        if (!onChange) {
            return;
        }
        const selectedIds = reduce(value, (set, id) => {
            set.add(id);
            return set;
        }, new Set());
        for (const item of items) {
            if (selected) {
                selectedIds.add(itemId(item));
            }
            else {
                selectedIds.delete(itemId(item));
            }
        }
        onChange(Array.from(selectedIds));
    }, [value, itemId, onChange]);
    const handleToggleExpanded = useCallback(e => {
        e.preventDefault();
        setExpanded(!expanded);
    }, [expanded]);
    const getGroupTitleKey = useCallback(groupIndex => {
        if (!groupTitleKeys || groupIndex >= groupTitleKeys.length) {
            return null;
        }
        return groupTitleKeys[groupIndex];
    }, [groupTitleKeys]);
    const flatOptions = useMemo(() => flatten(options), [
        options,
    ]);
    const { applyFilters } = useApplyFilter();
    useEffect(() => {
        if ((flatOptions === null || flatOptions === void 0 ? void 0 : flatOptions.length) === 1 &&
            onChange &&
            isEmpty(value) &&
            !isEmptyAllowed) {
            onChange(map(flatOptions, itemId));
            requestAnimationFrame(() => {
                applyFilters && applyFilters();
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applyFilters, flatOptions, itemId, onChange, isEmptyAllowed]);
    const handleSelectAllToggle = useCallback(selectAll => {
        if (!onChange) {
            return;
        }
        if (selectAll) {
            onChange(map(flatOptions, item => itemId(item)));
        }
        else {
            onChange([]);
        }
    }, [itemId, onChange, flatOptions]);
    const handleSearchReset = () => setSearchQuery('');
    const checkLiveSearch = useCallback(option => {
        if (!liveSearch || !searchQuery)
            return true;
        const field = isString(liveSearchField)
            ? liveSearchField
            : liveSearchField(option);
        if (!option[field])
            return false;
        return option[field]
            .toLocaleLowerCase()
            .includes(searchQuery.toLocaleLowerCase());
    }, [searchQuery, liveSearch, liveSearchField]);
    const itemsById = useMemo(() => reduce(flatOptions, (map, item) => {
        map.set(itemId(item), item);
        return map;
    }, new Map()), [itemId, flatOptions]);
    const selectedItems = useMemo(() => filter(map(value, id => itemsById.get(id)), item => !!item), [value, itemsById]);
    const selectedIds = useMemo(() => new Set(value), [value]);
    const allSelected = useMemo(() => selectedItems.length === flatOptions.length, [selectedItems.length, flatOptions.length]);
    const valueTitle = useMemo(() => {
        if (!icon) {
            const limit = 3;
            if (selectedItems.length === 0) {
                return t('None Selected');
            }
            else if (allSelected) {
                return t('All');
            }
            else if (hasCountBadge) {
                return [
                    ...map(take(selectedItems, badgeTitleNames), item => itemTitle(item)),
                    ...(selectedItems.length > badgeTitleNames ? ['...'] : []),
                ].join(', ');
            }
            else if (selectedItems.length > limit) {
                return t('#{count} selected', { count: selectedItems.length });
            }
            return selectedItems.map(item => itemTitle(item)).join(', ');
        }
        return '';
    }, [selectedItems, t, allSelected, icon, itemTitle, hasCountBadge]);
    const countBadgeTitle = useMemo(() => selectedItems.length > maxBadgeCount
        ? `${maxBadgeCount}+`
        : selectedItems.length, [selectedItems.length]);
    const orderedOptions = useMemo(() => orderOptionsBy
        ? [
            ...orderBy(filter(options, option => !Array.isArray(option) && checkLiveSearch(option)), [option => option[orderOptionsBy].toLocaleLowerCase()], ['asc']),
            ...filter(options, option => Array.isArray(option)).map(optionsArray => orderBy(filter(optionsArray, option => checkLiveSearch(option)), [option => { var _a; return toLower((_a = option[orderOptionsBy]) === null || _a === void 0 ? void 0 : _a.toString()); }], ['asc'])),
        ]
        : filter(options, checkLiveSearch), [options, orderOptionsBy, checkLiveSearch]);
    const isSelectAllShown = showSelectAll && isEmpty(searchQuery);
    return (<div ref={containerEl} className={classnames('dropdown status', classNames)}>
      <div className={classnames('btn-group', buttonClassName, {
        open: expanded,
        'w-100': isFullSizeButton,
    })}>
        <Ripple>
          <button aria-expanded={expanded} className={classnames('multiselect dropdown-toggle btn btn-default', {
        [styles.multiselectButtonRtl]: isLayoutRTL,
    })} data-cy={dataCy} disabled={disabled} title={getHelpNoteTooltipTitle(label ? t(label) : title || '')} type="button" onClick={handleToggleExpanded}>
            <span className="multiselect-selected-text">
              {icon && <Icon name={icon} type={iconType}/>}
              {title && <span className="text-semibold">{title}:&nbsp;</span>}
              {valueTitle && <span className="value ml-0">{valueTitle}</span>}
              {hasCountBadge && !isEmpty(selectedItems) && (<BadgeTooltip offsetTop={-20} position="responsive" title={countBadgeTitle} tooltipWrapperClassName={styles.countBadge}>
                  <ul className="list-unstyled no-margin">
                    {map(selectedItems, item => (<li key={`selected-${itemId(item)}`}>
                        {itemTitle(item)}
                      </li>))}
                  </ul>
                </BadgeTooltip>)}
              <span className={classnames('caret', styles.selectCaret, {
        [styles.selectCaretRtl]: isLayoutRTL,
    })}/>
            </span>
          </button>
        </Ripple>
        <Scroll className={classnames(`dropdown-menu dropdown-menu-${opens} active`, styles.positionFix)} classNameInner="multiselect-container dropdown-menu" isOpen={expanded} style={{ width: size }} 
    /* eslint-disable-next-line react/forbid-component-props */
    tagName="ul">
          {liveSearch && (<div className="search-field-wrapper">
              <SearchField isInTable className={styles.searchboxField} inputClassName={inputClassName} inputProps={{ autoFocus: true }} isDebouncingEnabled={false} isRtlPullRightEnabled={false} value={searchQuery} widthClassName={styles.searchbox} onChange={setSearchQuery} onReset={handleSearchReset}/>
            </div>)}
          {isSelectAllShown && [
        <MultiselectSelectAllItem key="selectAll" allSelected={allSelected} onToggle={handleSelectAllToggle}/>,
        <li key="selectAllDivider" className={hasSelectAllDivider ? 'divider' : ''}/>,
    ]}

          {map(orderedOptions, (item, itemIndex) => {
        if (Array.isArray(item)) {
            const groupId = `group-${map(item, itemId).join('-')}`;
            return (<MultiselectGroup key={groupId} first={itemIndex === 0} groupTitleKey={getGroupTitleKey(itemIndex)} itemId={itemId} items={item} itemStatus={itemStatus} itemTitle={itemTitle} labelDividerNone={labelDividerNone} maxLimitTooltip={maxLimitTooltip} selectedIds={selectedIds} showStatus={showStatus} textTransformNone={textTransformNone} onToggle={handleToggleItem} onToggleGroup={handleToggleItems}/>);
        }
        const id = itemId(item);
        const title = itemTitle(item);
        const status = itemStatus(item);
        const tooltipTitle = getHelpNoteTooltipTitle(typeof item.name === 'string' ? item.name : title);
        return (<MultiselectItem key={`item-${id}`} id={id} maxLimitTooltip={maxLimitTooltip} selected={id && selectedIds.has(id)} showStatus={showStatus} status={status} title={title} tooltipTitle={tooltipTitle} onToggle={handleToggleItem}/>);
    })}
          {children}
        </Scroll>
      </div>
    </div>);
}
const defaultItemTitle = ({ name }) => name;
const defaultItemId = ({ id }) => id;
const defaultItemStatus = ({ status }) => status;
const defaultSize = 100;
const badgeTitleNames = 2;
const maxBadgeCount = 99;
